import i18next, { InitOptions } from 'i18next';
import Backend, { HttpBackendOptions } from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { defaultI18Lng, getLanguageDirection, locales } from './config/i18n';
import { makeI18nFallback } from './config/i18nFallback';
import config from 'config/common';
import { getSearchParamFromUrl } from 'helpers/urlParamaters';
import { merge } from 'lodash';

const overrides = {};

enum DtOptionsEnum {
    KEY = 'KEY',
    VALUE = 'VALUE',
    KEY_VALUE = 'KEY_VALUE',
}
const postProcessLog = {
    name: 'nameOfProcessor',
    process: function (value, key, options, translator) {
        let params = new URLSearchParams(document.location.search);
        let dtOptions = params.get('dtOptions');
        if (dtOptions) {
            sessionStorage.setItem('dtOptions', dtOptions);
        }
        dtOptions = dtOptions ? dtOptions : sessionStorage.getItem('dtOptions');
        if (dtOptions) {
            switch (dtOptions) {
                case DtOptionsEnum.KEY:
                    return key;
                case DtOptionsEnum.VALUE:
                    return value;
                case DtOptionsEnum.KEY_VALUE:
                    return `${value} (${key})`;
            }
        }
        return value;
    },
};

export const i18nOptions: InitOptions = {
    postProcess: [postProcessLog.name],
    debug: false,
    fallbackLng: makeI18nFallback(defaultI18Lng),
    preload: [defaultI18Lng],
    lng: getSearchParamFromUrl('lang') || localStorage.getItem('i18nextLng') || defaultI18Lng,
    supportedLngs: config.supportedLanguages,
    defaultNS: 'common',
    ns: [
        'overrides',
        'account-settings',
        'kyc-broker-6',
        'client-support',
        'common',
        'components',
        'dashboard',
        'email-confirmation',
        'email-verification',
        'forgot-password',
        'kyc-broker-3',
        'graphIndicators',
        'kyc',
        'kyc-questions-6',
        'kyc-questions-3',
        'kyc-questions-9',
        'login',
        'msgPopup',
        'myWallet',
        'new-password',
        'notifications',
        'phone-verification',
        'register',
        'social',
        'kyc-questions-4',
        'trading-accounts',
        'trading-analysis',
        'upload-documents',
        'notification',
        'refer-friend',
        'prop',
        'send-magic-link',
    ],
    load: 'all',
    interpolation: { escapeValue: false },
    lowerCaseLng: true,
    react: { useSuspense: true },
    backend: {
        parse: (data, language: string, namespace: string) => {
            const dataObj = JSON.parse(data);
            if (namespace === 'overrides') {
                overrides[language] = dataObj;
                for (const overrideNamespace in overrides[language]) {
                    if (i18next.hasResourceBundle(language, overrideNamespace)) {
                        i18next.addResourceBundle(
                            language,
                            overrideNamespace,
                            merge(
                                i18next.getResourceBundle(language, overrideNamespace),
                                overrides[language][overrideNamespace]
                            ),
                            true,
                            true
                        );
                    }
                }
                return {};
            }

            if (overrides?.[language]?.[namespace]) {
                const mergedData = merge(dataObj, overrides[language][namespace]);
                return mergedData;
            }
            return dataObj;
        },
    } as HttpBackendOptions,
};
i18next
    .use(Backend)
    .use({ type: 'postProcessor', name: postProcessLog.name, process: postProcessLog.process })
    .use(new LanguageDetector(null, { lookupQuerystring: 'lang' }))
    .use(initReactI18next)
    .init(i18nOptions);

function applyDocumentDirection(lng: string) {
    const dir = getLanguageDirection(lng);
    document.body.dir = dir;
    document.documentElement.setAttribute('lang', lng);
}

export function i18nPrepareDocumentForRender() {
    applyDocumentDirection(i18nOptions.lng as string);
    i18next.on('languageChanged', (newLng: string) => {
        applyDocumentDirection(newLng);
    });
}

export const onChangeLng = (lng: string) => {
    if (!Object.keys(locales).some((language) => lng === language)) {
        const cleanedLanguage = lng;
        const cleanedLanguageIsFound = Object.keys(locales).some((language) => cleanedLanguage === language);
        const newLanguage = cleanedLanguageIsFound ? cleanedLanguage : defaultI18Lng;
        return i18next.changeLanguage(newLanguage);
    }
    // changePlatformSrc(undefined, lng, undefined);
    const html = document.getElementsByTagName('html')[0];

    html.setAttribute('lang', lng);
    let dir = 'ltr';
    try {
        dir = locales[lng].dir;
    } catch {}
    try {
        document.body.dir = dir;
        return { direction: dir };
    } catch {}
};

window['i18ntest'] = i18next;

export default i18next;
